/**
 * Column catalogue + export projection for the SDMS Result Validation /
 * Approval detail grids (Batch Results, Check Points).
 *
 * ONE source of truth for each grid's natural column order and header labels:
 * the grid renders `columnIds(...)` and the tab-bar "Export" button projects the
 * SAME ids through the same labels, so an export can never drift from the header
 * row it claims to mirror.
 *
 * Kept pure and dependency-free (§18.4) so the ordering + projection rules are
 * unit-testable without rendering the 3.7k-line SdmsPackageDetail component.
 * The actual file writing is the shared <ExportMenu> (§14b / standing rule 8) —
 * this module only shapes { headers, rows }.
 */

import type { GridPrefs } from '../../components/AppDataGrid';

/** One grid column. `action` marks a link/button column (RawData "Preview") —
 *  rendered in the grid, never exported (it carries no data). */
export interface SdmsExportColumn {
  id: string;
  label: string;
  action?: boolean;
}

/**
 * Batch Results columns in ALPACA's natural order.  `sys` (SysSampleCode) is
 * FROZEN first in the grid (rendered outside the reorderable set) and so is
 * always the first export column too.
 *
 * @param showApprovedCols Whether the ApprovedDate / ApprovedBy pair is in play
 *                         (Default Settings › "SDMS Result Approval", or the
 *                         Approval form itself) — see showApprovedColumns().
 */
export function batchResultColumnCatalog(showApprovedCols: boolean): SdmsExportColumn[] {
  return [
    { id: 'sys', label: 'SysSampleCode' },
    { id: 'param', label: 'Parameter' },
    { id: 'numeric', label: 'NumericResult' },
    { id: 'result', label: 'Result' },
    { id: 'units', label: 'Units' },
    { id: 'analyzedDate', label: 'AnalyzedDate' },
    { id: 'analyzedBy', label: 'AnalyzedBy' },
    { id: 'submittedDate', label: 'SubmittedDate' },
    { id: 'submittedBy', label: 'SubmittedBy' },
    { id: 'validatedDate', label: 'ValidatedDate' },
    { id: 'validatedBy', label: 'ValidatedBy' },
    ...(showApprovedCols
      ? [
          { id: 'approvedDate', label: 'ApprovedDate' },
          { id: 'approvedBy', label: 'ApprovedBy' },
        ]
      : []),
    { id: 'dateCollected', label: 'DateCollected' },
    { id: 'collector', label: 'Collector' },
    { id: 'dateReceived', label: 'DateReceived' },
    { id: 'sampleName', label: 'SampleName' },
    { id: 'siteName', label: 'SiteName' },
    { id: 'rawData', label: 'RawData', action: true },
    { id: 'client', label: 'Client' },
    { id: 'projectId', label: 'ProjectID' },
    { id: 'df', label: 'DF' },
    { id: 'rptlimit', label: 'RPTLIMIT' },
    { id: 'mdl', label: 'MDL' },
    { id: 'uql', label: 'UQL' },
    { id: 'mcl', label: 'MCL' },
    { id: 'loq', label: 'LOQ' },
    { id: 'spike', label: 'SpikeAmount' },
    { id: 'dld', label: 'DLD' },
    { id: 'rpd', label: 'RPD' },
    { id: 'rpdCtrl', label: 'RPDCtrlLimit' },
    { id: 'rec', label: '%Rec' },
    { id: 'recCtrl', label: 'RecCtrlLimit' },
    { id: 'qualifier', label: 'Qualifier' },
    { id: 'qc', label: 'QCStatus' },
    { id: 'enteredBy', label: 'EnteredBy' },
    { id: 'enteredDate', label: 'EnteredDate' },
  ];
}

/** Check Points columns in ALPACA's natural order. */
export function checkPointColumnCatalog(): SdmsExportColumn[] {
  return [
    { id: 'checkPoint', label: 'CheckPoint' },
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
    { id: 'na', label: 'NA' },
    { id: 'qualifier', label: 'Qualifier' },
    { id: 'comment', label: 'Comment' },
  ];
}

/** The Batch Results column that stays pinned first (never drag-reorderable). */
export const BR_FROZEN_ID = 'sys';

/**
 * Reorderable column ids for a grid — the catalogue minus the frozen leader.
 * The grid feeds this to `applyOrder(...)`; keeping it here means the grid and
 * the exporter enumerate the same columns.
 */
export function reorderableIds(catalog: readonly SdmsExportColumn[], frozenId?: string): string[] {
  return catalog.map((c) => c.id).filter((id) => id !== frozenId);
}

/**
 * The columns an export should contain, in the order the analyst currently sees
 * them: the saved column order applied (unknown ids dropped, new ids appended in
 * natural order — the same rule as the grid's `applyOrder`), hidden columns
 * removed, and action columns (RawData) skipped.
 *
 * The frozen leader stays first regardless of the saved order, matching the grid.
 */
export function visibleExportColumns(
  catalog: readonly SdmsExportColumn[],
  prefs: GridPrefs,
  frozenId?: string,
): SdmsExportColumn[] {
  const byId = new Map(catalog.map((c) => [c.id, c]));
  const order = prefs.order ?? [];
  const movable = catalog.map((c) => c.id).filter((id) => id !== frozenId);
  const known = new Set(movable);
  const ordered =
    order.length > 0
      ? [...order.filter((id) => known.has(id)), ...movable.filter((id) => !order.includes(id))]
      : movable;
  const ids = frozenId && byId.has(frozenId) ? [frozenId, ...ordered] : ordered;

  const hidden = new Set(prefs.hidden ?? []);
  return ids
    .map((id) => byId.get(id))
    .filter((c): c is SdmsExportColumn => !!c && !c.action && !hidden.has(c.id));
}

/**
 * Project rows through the visible columns into the flat
 * `{ headers, rows }` shape the shared <ExportMenu> writes out.
 *
 * @param text Per-cell display text for (row, columnId) — the caller passes the
 *             grid's own formatter (with any in-session cell edits folded in) so
 *             the file matches the screen.
 */
export function buildGridExport<T>(
  columns: readonly SdmsExportColumn[],
  rows: readonly T[],
  text: (row: T, columnId: string) => string,
): { headers: string[]; rows: string[][] } {
  return {
    headers: columns.map((c) => c.label),
    rows: rows.map((r) => columns.map((c) => text(r, c.id) ?? '')),
  };
}

/**
 * Review Log columns (bottom card, "Review Log" tab) in the order the grid renders
 * them. ElectronicSignature is a rendered CARD in the grid (signer, stamp, hash,
 * Verified badge); its RL_FMT text — signer + timestamp — is what exports, which is
 * the part a reader can act on.
 */
export function reviewLogColumnCatalog(): SdmsExportColumn[] {
  return [
    { id: 'processMode', label: 'ProcessMode' },
    { id: 'role', label: 'Role' },
    { id: 'decisionStatus', label: 'DecisionStatus' },
    { id: 'reviewScope', label: 'ReviewScope' },
    { id: 'comment', label: 'Comment' },
    { id: 'electronicSignature', label: 'ElectronicSignature' },
  ];
}

/** Attachments columns (bottom card, "Attachments" tab). */
export function attachmentColumnCatalog(): SdmsExportColumn[] {
  return [
    { id: 'name', label: 'Name' },
    { id: 'category', label: 'Category' },
    { id: 'file', label: 'Attachment' },
    { id: 'date', label: 'Date' },
    { id: 'operator', label: 'Operator' },
  ];
}
